import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  InputNumber,
  Spin,
  Row,
  Col,
  Space,
  Tag,
  Tooltip,
  Modal,
} from "antd";
import API from "../../../../services/api";
import { uploadToCloudinary, uploadMultipleToCloudinary } from "../../../../utils/uploadToCloudinary";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LocationField from "../../../LocationField";
import {
  EnvironmentOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./EditStall.css";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STALL_TYPES = ["food", "retail", "exhibition", "service", "other"];

// Duration options per pricing type:
// rent  → daily, weekly, monthly, yearly
// lease → monthly, yearly
// sale  → (none — no duration shown)
const DURATION_TYPES_BY_PRICING = {
  rent: ["daily", "weekly", "monthly", "yearly"],
  lease: ["monthly", "yearly"],
  sale: [],
};

const QUICK_FACILITIES = [
  "Power Supply",
  "Water Supply",
  "Seating Area",
  "Restroom Nearby",
  "Waste Management",
  "Lighting",
  "Ventilation",
  "Storage Space",
  "Parking",
  "Security",
  "Wi-Fi",
  "Fire Safety",
];

function EditStall() {
  const [form] = Form.useForm();

  const stallTypeValue = Form.useWatch("stallType", form);
  const pricingTypeValue = Form.useWatch("pricingType", form);
  const durationTypeValue = Form.useWatch("durationType", form);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [removedImages, setRemovedImages] = useState([]);
  const [stepChanging, setStepChanging] = useState(false);
  const [removedThumbnail, setRemovedThumbnail] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Custom stall type (mirrors AddStall)
  const [customStallType, setCustomStallType] = useState("");
  const [showCustomStallType, setShowCustomStallType] = useState(false);

  // Facilities state (mirrors AddStall pattern)
  const [facilitiesList, setFacilitiesList] = useState([]);
  const [customFacility, setCustomFacility] = useState("");
  const [clickedFacility, setClickedFacility] = useState(null);

  // Image state
  const [thumbnailList, setThumbnailList] = useState([]);
  const [fileList, setFileList] = useState([]);

  const abortControllerRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Same step visibility logic as AddStall
  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  // Steps — Duration merged into Pricing (step 2)
  const steps = [
    "Description",
    "Details",
    "Pricing & Duration",
    "Location",
    "Facilities",
    "Contact",
    "Images",
  ];

  const stepFields = [
    ["title", "description"],                                                              // Step 0
    ["stallType", "areaSqFt", "allowedBusinessTypes"],                                    // Step 1
    ["pricingType", "durationType", "startDate", "endDate", "price", "deposit", "negotiable"], // Step 2
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],           // Step 3
    ["facilities"],                                                                        // Step 4
    ["contactName", "phone", "email"],                                                     // Step 5
    ["thumbnail", "images"],                                                               // Step 6
  ];

  // ── Facility helpers (mirrors AddStall) ──────────────────────────────────
  const addQuickFacility = (facility) => {
    setClickedFacility(facility);
    setTimeout(() => setClickedFacility(null), 300);

    if (!facilitiesList.includes(facility)) {
      const newFacilities = [...facilitiesList, facility];
      setFacilitiesList(newFacilities);
      form.setFieldsValue({ facilities: newFacilities.join(", ") });
      form.validateFields(["facilities"]);

    }
  };

  const addCustomFacility = () => {
    const facility = customFacility.trim();
    if (!facility) {
      message.warning("Please enter a facility name");
      return;
    }
    if (!/^[A-Za-z\s.-]+$/.test(facility)) {
      message.error("Facility name can only contain letters, spaces, . and -");
      return;
    }
    if (!facilitiesList.includes(facility)) {
      const newFacilities = [...facilitiesList, facility];
      setFacilitiesList(newFacilities);
      form.setFieldsValue({ facilities: newFacilities.join(", ") });
      form.validateFields(["facilities"]);
      setCustomFacility("");
    }
  };

  const removeFacility = (facilityToRemove) => {
    const newFacilities = facilitiesList.filter((f) => f !== facilityToRemove);
    setFacilitiesList(newFacilities);
    form.setFieldsValue({ facilities: newFacilities.join(", ") });
    form.validateFields(["facilities"]);

  };

  // ── Step navigation ─────────────────────────────────────────────────────
  const next = async () => {
    // Custom stall type check (step 1)
    if (current === 1) {
      if (showCustomStallType && (!customStallType || customStallType.trim() === "")) {
        message.error("Please enter custom stall type");
        return;
      }
    }

    // Pricing & Duration check (step 2)
    if (current === 2) {
      const pricingType = form.getFieldValue("pricingType");
      if (pricingType === "rent") {
        const deposit = form.getFieldValue("deposit");
        if (!deposit) {
          message.error("Please enter deposit amount for rent");
          return;
        }
      }
    }

    // Facilities check (step 4)
    if (current === 4) {
      if (facilitiesList.length === 0) {
        message.error("Please add at least one facility");
        return;
      }
      setCurrent(current + 1);
      window.scrollTo(0, 0);
      return;
    }

    // Images check (step 6)
    if (current === 6) {
      if (thumbnailList.length === 0) {
        message.error("Please upload a thumbnail image");
        return;
      }
      if (fileList.length === 0) {
        message.error("Please upload at least one image");
        return;
      }
    }

    try {
      // For step 2 (Pricing & Duration), validate duration fields conditionally
      if (current === 2) {
        const pricingType = form.getFieldValue("pricingType");
        let fieldsToValidate = ["pricingType", "price", "negotiable"];
        if (pricingType === "rent") {
          fieldsToValidate = [...fieldsToValidate, "deposit", "durationType", "startDate", "endDate"];
        } else if (pricingType === "lease") {
          fieldsToValidate = [...fieldsToValidate, "durationType", "startDate", "endDate"];
        }
        // sale → no duration fields needed
        await form.validateFields(fieldsToValidate);
      } else {
        await form.validateFields(stepFields[current]);
      }

      setCurrent(current + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      const firstError =
        err.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
      message.error({ content: firstError, key: "validation-error", duration: 2.5 });
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const goToStep = useCallback(
    (stepIndex) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setStepChanging(true);
        setCurrent(stepIndex);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          setStepChanging(false);
        }, 100);
      }
    },
    [steps.length]
  );

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

  const normalizeUpload = (e) => e?.fileList || [];

  const createUploadFile = (file, index = 0) => {
    if (!file) return null;
    if (typeof file === "string") {
      return {
        uid: `existing-${index}-${Date.now()}`,
        name: `image-${index + 1}`,
        status: "done",
        url: file,
      };
    }
    return {
      uid: file._id || file.uid || `existing-${index}-${Date.now()}`,
      name: file.name || `image-${index + 1}`,
      status: "done",
      url: file.url || file.path || file.location || file.secure_url,
    };
  };

  // ── Fetch existing stall data ─────────────────────────────────────────────
  const fetchStall = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setPageLoading(true);
      setPageError(false);
      const res = await API.get(`/stalls/${id}`, {
        signal: abortControllerRef.current.signal,
      });
      const item = res.data?.data || res.data;

      const thumbnailFileList = item?.thumbnail
        ? [createUploadFile(item.thumbnail, 0)].filter(Boolean)
        : [];

      const imagesFileList = Array.isArray(item?.images)
        ? item.images.map((img, index) => createUploadFile(img, index)).filter(Boolean)
        : [];

      // Restore facilities list
      const existingFacilities = Array.isArray(item?.facilities)
        ? item.facilities.filter((f) => typeof f === "string")
        : [];
      setFacilitiesList(existingFacilities);

      // Restore custom stall type if needed
      const stallType = item?.stallDetails?.stallType || "";
      if (stallType && !STALL_TYPES.includes(stallType)) {
        setShowCustomStallType(true);
        setCustomStallType(stallType);
      }

      const allowedBusinessTypesValue = Array.isArray(item?.stallDetails?.allowedBusinessTypes)
        ? item.stallDetails.allowedBusinessTypes.join(", ")
        : "";

      setThumbnailList(thumbnailFileList);
      setFileList(imagesFileList);

      const existingPricingType = item?.pricing?.type || "rent";

      form.setFieldsValue({
        title: item?.title || "",
        description: item?.description || "",
        stallType: item?.stallDetails?.stallType || undefined,
        areaSqFt: item?.stallDetails?.areaSqFt ?? undefined,
        allowedBusinessTypes: allowedBusinessTypesValue,
        // Only set durationType/startDate/endDate if not sale
        durationType: existingPricingType !== "sale"
          ? (item?.durationDetails?.durationType || undefined)
          : undefined,
        startDate: existingPricingType !== "sale" && item?.durationDetails?.startDate
          ? item.durationDetails.startDate.slice(0, 10)
          : "",
        endDate: existingPricingType !== "sale" && item?.durationDetails?.endDate
          ? item.durationDetails.endDate.slice(0, 10)
          : "",
        address: item?.location?.address || "",
        locality: item?.location?.locality || "",
        city: item?.location?.city || "",
        state: item?.location?.state || "",
        country: item?.location?.country || "",
        pincode: item?.location?.pincode || "",
        latitude: item?.location?.coordinates?.coordinates?.[1] ?? undefined,
        longitude: item?.location?.coordinates?.coordinates?.[0] ?? undefined,
        pricingType: existingPricingType,
        price: item?.pricing?.price ?? undefined,
        deposit: item?.pricing?.deposit ?? undefined,
        negotiable: !!item?.pricing?.negotiable,
        facilities: existingFacilities.join(", "),
        contactName: item?.contactDetails?.contactName || "",
        phone: item?.contactDetails?.phone || "",
        email: item?.contactDetails?.email || "",
        thumbnail: thumbnailFileList,
        images: imagesFileList,
      });
      setIsFormDirty(false);
    } catch (err) {
      if (err.name !== "AbortError" && err.name !== "CanceledError") {
        setPageError(true);
        message.error({
          content: err.response?.data?.message || "Failed to load stall details",
        });
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchStall();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [id]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".edit-stall-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  const handleBack = () => {
    if (isFormDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate("/my-stalls");
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFinish = async (values) => {
    if (thumbnailList.length === 0) {
      message.error("Please upload a thumbnail image before updating");
      return;
    }
    if (fileList.length === 0) {
      message.error("Please upload at least one gallery image before updating");
      return;
    }
    if (fileList.length > 5) {
      message.error("Maximum 5 images allowed");
      return;
    }

    try {
      setLoading(true);

      const pricingType = values.pricingType || "rent";

      const allowedBusinessTypes = values.allowedBusinessTypes
        ? values.allowedBusinessTypes
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean)
        : [];

      const data = {
        title: values.title,
        description: values.description,
        stallDetails: {
          stallType: values.stallType,
          areaSqFt: Number(values.areaSqFt),
          allowedBusinessTypes,
        },
        // For sale: clear duration; for rent/lease: use chosen values
        durationDetails:
          pricingType === "sale"
            ? { durationType: null, startDate: null, endDate: null }
            : {
              durationType: values.durationType,
              startDate: values.startDate,
              endDate: values.endDate,
            },
        location: {
          address: values.address,
          locality: values.locality,
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
        },
        pricing: {
          type: pricingType,
          price: Number(values.price),
          ...(pricingType === "rent" && values.deposit
            ? { deposit: Number(values.deposit) }
            : {}),
          negotiable: !!values.negotiable,
        },
        facilities: facilitiesList,
        contactDetails: {
          contactName: values.contactName,
          phone: values.phone,
          email: values.email,
        },
        deleteThumbnail: removedThumbnail,
        deleteImages: removedImages,
        imageOrder: fileList.filter((f) => !f.originFileObj).map((f) => f.url),
      };

      // Upload new thumbnail if changed
      const uploadedThumb = thumbnailList[0]?.originFileObj
        ? await uploadToCloudinary(thumbnailList[0].originFileObj, "stalls")
        : null;

      // Upload new gallery images
      const newFilesToUpload = fileList.filter((f) => f.originFileObj);
      const uploadedImages = newFilesToUpload.length
        ? await uploadMultipleToCloudinary(
          newFilesToUpload.map((f) => f.originFileObj),
          "stalls"
        )
        : [];

      await API.put(`/stalls/${id}`, {
        ...data,

        newThumbnail: uploadedThumb?.url || uploadedThumb,

        newImages: uploadedImages.map(img => img.url || img),
      });
      setIsFormDirty(false);

      message.success({ content: "Stall updated successfully" });
      setRemovedImages([]);
      setRemovedThumbnail(false);
      setTimeout(() => navigate("/my-stalls"), 500);
    } catch (err) {
      message.error({
        content: err.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isMobile = windowWidth <= 768;

  if (!pageLoading && pageError) {
    return (
      <div className="edit-stall-page">
        <div className="edit-stall-container">
          <Card className="edit-stall-card">
            <div className="edit-stall-error-state">
              <ExclamationCircleOutlined className="edit-stall-error-icon" />
              <h2>Failed to load stall details</h2>
              <p>Something went wrong while fetching the data. Please try again.</p>
              <Button type="primary" onClick={fetchStall}>
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-stall-page">
      <div className="edit-stall-container">
        <Spin
          spinning={pageLoading || stepChanging}
          description={stepChanging ? "Loading step..." : "Loading stall details..."}
        >
          {/* Unsaved changes modal */}
          <Modal
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <span>Unsaved Changes</span>
              </Space>
            }
            open={showUnsavedModal}
            onOk={() => { setShowUnsavedModal(false); navigate("/my-stalls"); }}
            onCancel={() => setShowUnsavedModal(false)}
            okText="Leave"
            cancelText="Stay and Continue Editing"
            okButtonProps={{ danger: true }}
            width={isMobile ? "90%" : 520}
          >
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <Text type="secondary">Your changes will be lost if you don't save them.</Text>
          </Modal>

          <Card className="edit-stall-card">
            {/* Header */}
            <div className="edit-stall-card-header">
              <div className="edit-stall-card-header-left">
                <Tag className="edit-stall-edit-badge">Editing Mode</Tag>
                <h1 className="edit-stall-title">Edit Stall</h1>
                <p className="edit-stall-subtitle">Update your stall listing details</p>
              </div>
              <Button
                icon={<CloseOutlined />}
                className="edit-stall-back-btn"
                onClick={handleBack}
                aria-label="Go back"
              >
                {!isMobile && "Back to Listing"}
              </Button>
            </div>

            {/* Stepper */}
            <div className="edit-stall-stepper">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className={`edit-stall-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
                  onClick={() => goToStep(i)}
                  onKeyDown={(e) => handleKeyDown(e, () => goToStep(i))}
                  role="listitem"
                  tabIndex={0}
                  aria-label={`Step ${i + 1}: ${step}`}
                >
                  <div className="edit-stall-step-line-left" />
                  <div className="edit-stall-step-inner">
                    <div className="edit-stall-circle">
                      {current > i ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className="edit-stall-step-label">{step}</span>
                  </div>
                  <div className="edit-stall-step-line-right" />
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="edit-stall-progress-bar">
              <div
                className="edit-stall-progress-fill"
                style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
              />
            </div>
            <div className="edit-stall-progress-label">
              Step {current + 1} of {steps.length} — {steps[current]}
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={() => setIsFormDirty(true)}
              scrollToFirstError
              validateTrigger="onBlur"
              className="edit-stall-form"
            >
              {/* Step 0: Description */}
              <div style={getStepStyle(0)}>
                <Form.Item
                  name="title"
                  label="Stall Title"
                  rules={[
                    { required: true, message: "Please enter a title for your stall" },
                    { min: 5, message: "Title must be at least 5 characters long" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.#&-]*$/,
                      message:
                        "Title must include at least one letter and can contain numbers or symbols like # . & -",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Premium Food Stall at City Mall" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: "Please provide a description" },
                    { min: 20, message: "Description should be at least 20 characters" },
                  ]}
                >
                  <TextArea
                    rows={5}
                    placeholder="Describe your stall, business potential, location advantages, and unique selling points..."
                  />
                </Form.Item>
              </div>

              {/* Step 1: Details */}
              <div style={getStepStyle(1)}>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="stallType"
                      label="Stall Type"
                      rules={[{ required: true, message: "Please select a stall type" }]}
                    >
                      <div
                        className={`edit-stall-custom-select ${openDropdown === "stallType"
                          ? "active"
                          : ""
                          }`}
                      >
                        <div
                          className="edit-stall-custom-select-trigger"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === "stallType"
                                ? null
                                : "stallType"
                            )
                          }
                        >
                          {stallTypeValue
                            ? stallTypeValue.charAt(0).toUpperCase() +
                            stallTypeValue.slice(1)
                            : "Select stall type"}

                          <span className="edit-stall-custom-arrow">
                            ⌄
                          </span>
                        </div>

                        {openDropdown === "stallType" && (
                          <div className="edit-stall-custom-dropdown">
                            {STALL_TYPES.map((item) => (
                              <div
                                key={item}
                                className="edit-stall-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldsValue({
                                    stallType: item,
                                  });

                                  if (item === "other") {
                                    setShowCustomStallType(true);
                                  } else {
                                    setShowCustomStallType(false);
                                    setCustomStallType("");
                                  }

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.charAt(0).toUpperCase() +
                                  item.slice(1)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Form.Item>
                    {showCustomStallType && (
                      <Form.Item
                        name="customStallType"
                        label="Enter Custom Stall Type"
                        rules={[
                          { required: true, message: "Please enter custom stall type" },
                          { min: 2, message: "Stall type must be at least 2 characters" },
                          {
                            pattern: /^[A-Za-z][A-Za-z\s&-]*$/,
                            message: "Start with letter, can include letters, spaces, & and -",
                          },
                        ]}
                        style={{ marginTop: -8 }}
                      >
                        <Input
                          placeholder="e.g. Art & Craft, Furniture, Electronics"
                          value={customStallType}
                          onChange={(e) => {
                            setCustomStallType(e.target.value);
                            form.setFieldsValue({
                              stallType: e.target.value,
                            });
                          }}
                        />
                      </Form.Item>
                    )}
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="areaSqFt"
                      label="Area (sq ft)"
                      rules={[
                        { required: true, message: "Please enter the area in sq ft" },
                        { type: "number", min: 10, message: "Area must be at least 10 sq ft" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 120" min={10} />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="allowedBusinessTypes"
                      label="Allowed Business Types"
                      extra="Separate each business type with a comma — e.g. Food, Retail, Electronics"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const items = value.split(",").map((v) => v.trim()).filter(Boolean);
                            const invalid = items.some((v) => !/^[A-Za-z\s]+$/.test(v));
                            if (invalid) return Promise.reject("Business types should only contain letters");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Food, Retail, Electronics, etc." />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 2: Pricing & Duration (MERGED) */}
              <div style={getStepStyle(2)}>
                {/* Pricing Type */}
                <Form.Item
                  name="pricingType"
                  label="Pricing Type"
                  rules={[{ required: true, message: "Please select a pricing type" }]}
                >
                  <div
                    className={`edit-stall-custom-select ${openDropdown === "pricingType"
                      ? "active"
                      : ""
                      }`}
                  >
                    <div
                      className="edit-stall-custom-select-trigger"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "pricingType"
                            ? null
                            : "pricingType"
                        )
                      }
                    >
                      {pricingTypeValue
                        ? pricingTypeValue.charAt(0).toUpperCase() +
                        pricingTypeValue.slice(1)
                        : "Select pricing type"}

                      <span className="edit-stall-custom-arrow">
                        ⌄
                      </span>
                    </div>

                    {openDropdown === "pricingType" && (
                      <div className="edit-stall-custom-dropdown">
                        {["rent", "lease", "sale"].map((item) => (
                          <div
                            key={item}
                            className="edit-stall-custom-dropdown-item"
                            onClick={() => {
                              form.setFieldsValue({
                                pricingType: item,
                              });

                              setOpenDropdown(null);
                            }}
                          >
                            {item.charAt(0).toUpperCase() +
                              item.slice(1)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Item>

                {/* Duration Type — shown only for rent and lease */}
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => prev.pricingType !== curr.pricingType}
                >
                  {({ getFieldValue }) => {
                    const pricingType = getFieldValue("pricingType");
                    if (pricingType === "sale" || !pricingType) return null;

                    const availableDurations = DURATION_TYPES_BY_PRICING[pricingType] || [];

                    return (
                      <Form.Item
                        name="durationType"
                        label="Duration Type"
                        rules={[{ required: true, message: "Please select a duration type" }]}
                      >
                        <div
                          className={`edit-stall-custom-select ${openDropdown === "durationType"
                            ? "active"
                            : ""
                            }`}
                        >
                          <div
                            className="edit-stall-custom-select-trigger"
                            onClick={() =>
                              setOpenDropdown(
                                openDropdown === "durationType"
                                  ? null
                                  : "durationType"
                              )
                            }
                          >
                            {durationTypeValue
                              ? durationTypeValue.charAt(0).toUpperCase() +
                              durationTypeValue.slice(1)
                              : "Select duration type"}
                            <span className="edit-stall-custom-arrow">
                              ⌄
                            </span>
                          </div>

                          {openDropdown === "durationType" && (
                            <div className="edit-stall-custom-dropdown">
                              {DURATION_TYPES_BY_PRICING[
                                pricingTypeValue
                              ]?.map((item) => (
                                <div
                                  key={item}
                                  className="edit-stall-custom-dropdown-item"
                                  onClick={() => {
                                    form.setFieldsValue({
                                      durationType: item,
                                    });

                                    setOpenDropdown(null);
                                  }}
                                >
                                  {item.charAt(0).toUpperCase() +
                                    item.slice(1)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Form.Item>
                    );
                  }}
                </Form.Item>

                {/* Start & End Date — shown only for rent and lease */}
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => prev.pricingType !== curr.pricingType}
                >
                  {({ getFieldValue }) => {
                    const pricingType = getFieldValue("pricingType");
                    if (pricingType === "sale" || !pricingType) return null;

                    return (
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="startDate"
                            label="Start Date"
                            rules={[{ required: true, message: "Please select start date" }]}
                          >
                            <Input type="date" placeholder="YYYY-MM-DD" />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="endDate"
                            label="End Date"
                            dependencies={["startDate"]}
                            rules={[
                              { required: true, message: "Please select end date" },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const startDate = getFieldValue("startDate");
                                  if (!value || !startDate) return Promise.resolve();
                                  if (new Date(value) <= new Date(startDate)) {
                                    return Promise.reject("End date must be after start date");
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <Input type="date" placeholder="YYYY-MM-DD" />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  }}
                </Form.Item>

                {/* Price & Deposit */}
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="price"
                      label="Price (₹)"
                      rules={[
                        { required: true, message: "Please enter the price" },
                        { type: "number", min: 1, message: "Price must be greater than 0" },
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g. 25000" min={1} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) =>
                        prev.pricingType !== curr.pricingType || prev.price !== curr.price
                      }
                    >
                      {({ getFieldValue }) => {
                        const pricingType = getFieldValue("pricingType");
                        if (pricingType === "rent") {
                          return (
                            <Form.Item
                              name="deposit"
                              label="Deposit (₹)"
                              rules={[
                                { required: true, message: "Please enter the deposit amount" },
                                { type: "number", min: 0, message: "Deposit cannot be negative" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const price = getFieldValue("price");
                                    if (
                                      value !== undefined &&
                                      price !== undefined &&
                                      value !== null &&
                                      value <= price
                                    ) {
                                      return Promise.reject(
                                        new Error("Deposit must be greater than price")
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                              ]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                placeholder="e.g. 50000"
                                min={0}
                              />
                            </Form.Item>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="Negotiable" className="edit-stall-switch-item">
                  <Form.Item name="negotiable" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <span className="edit-stall-switch-label">Price is open to negotiation</span>
                </Form.Item>
              </div>

              {/* Step 3: Location */}
              <div style={getStepStyle(3)}>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[{ required: true, message: "Please enter the Address" }]}
                >
                  <Input placeholder="e.g. Shop No. 45, City Mall, Indiranagar, Bengaluru" />
                </Form.Item>

                <Form.Item
                  name="locality"
                  label="Locality"
                  rules={[
                    { required: true, message: "Please enter locality" },
                    { min: 2, message: "Minimum 2 characters" },
                    {
                      pattern: /^[A-Za-z0-9\s().-]*$/,
                      message:
                        "Locality can only contain letters, numbers, spaces, (), . and -",
                    },
                  ]}
                >
                  <LocationField
                    form={form}
                    message={message}
                    setAutoDetecting={setAutoDetecting}
                  />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="city"
                      label="City"
                      rules={[
                        { required: true, message: "Please enter the city" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "City name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. Bengaluru" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="state"
                      label="State"
                      rules={[
                        { required: true, message: "Please enter the State" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "State name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. Karnataka" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[
                        { required: true, message: "Please enter the country" },
                        {
                          pattern: /^[A-Za-z\s.-]+$/,
                          message: "Country name should contain only letters",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. India" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="pincode"
                      label="Pincode"
                      rules={[
                        { required: true, message: "Please enter the pincode" },
                        {
                          pattern: /^[1-9][0-9]{5}$/,
                          message: "Enter a valid 6-digit pincode",
                        },
                      ]}
                    >
                      <Input
                        maxLength={6}
                        placeholder="e.g. 560038"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="latitude"
                      label="Latitude"
                      rules={[
                        { required: true, message: "Please enter latitude" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null)
                              return Promise.reject("Latitude is required");

                            const str = value.toString();

                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");

                            const parts = str.split(".");

                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");

                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid latitude");

                            if (value < -90 || value > 90)
                              return Promise.reject("Latitude must be between -90 and 90");

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-90}
                        max={90}
                        step={0.000001}
                        placeholder="e.g. 12.971891"
                        onBlur={() => {
                          const val = form.getFieldValue("latitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="longitude"
                      label="Longitude"
                      rules={[
                        { required: true, message: "Please enter longitude" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null)
                              return Promise.reject("Longitude is required");

                            const str = value.toString();

                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");

                            const parts = str.split(".");

                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");

                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid longitude");

                            if (value < -180 || value > 180)
                              return Promise.reject("Longitude must be between -180 and 180");

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-180}
                        max={180}
                        step={0.000001}
                        placeholder="e.g. 77.594563"
                        onBlur={() => {
                          const val = form.getFieldValue("longitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 4: Facilities — mirrors AddStall's facilities UI exactly */}
              <div style={getStepStyle(4)}>
                <Form.Item
                  name="facilities"
                  rules={[
                    {
                      validator: () => {
                        if (facilitiesList.length === 0) {
                          return Promise.reject(
                            new Error("Please add at least one facility")
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                  hidden
                >
                  <Input />
                </Form.Item>
                <div className="edit-stall-step-heading">
                  <h2 className="required-heading">Facilities</h2>
                  <p>Select available facilities for your stall</p>
                </div>

                {/* QUICK FACILITIES */}
                <div className="edit-facilities-quick">
                  {QUICK_FACILITIES.map((facility) => (
                    <button
                      key={facility}
                      type="button"
                      className={`edit-facility-chip ${facilitiesList.includes(facility) ? "active" : ""
                        }`}
                      onClick={() => addQuickFacility(facility)}
                    >
                      {facility}
                      {facilitiesList.includes(facility) && (
                        <CheckOutlined className="edit-facility-check" />
                      )}
                    </button>
                  ))}
                </div>

                {/* CUSTOM FACILITY */}
                <div className="edit-facilities-custom">
                  <Input
                    placeholder="Add custom facility"
                    value={customFacility}
                    onChange={(e) => setCustomFacility(e.target.value)}
                    onPressEnter={addCustomFacility}
                  />
                  <Button type="primary" onClick={addCustomFacility}>
                    Add
                  </Button>
                </div>

                {/* SELECTED FACILITIES */}
                <div className="edit-facilities-selected">
                  {facilitiesList.map((facility) => (
                    <Tag
                      key={facility}
                      closable
                      onClose={() => removeFacility(facility)}
                    >
                      {facility}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* Step 5: Contact */}
              <div style={getStepStyle(5)}>
                <Form.Item
                  name="contactName"
                  label="Contact Person Name"
                  rules={[
                    { required: true, message: "Please enter contact name" },
                    {
                      pattern: /^[A-Za-z\s.-]+$/,
                      message: "Name can only contain letters and basic characters",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Rajesh Kumar" />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Please enter a phone number" },
                        {
                          pattern: /^[6-9]\d{9}$/,
                          message: "Enter a valid 10-digit Indian phone number",
                        },
                      ]}
                    >
                      <Input maxLength={10} placeholder="e.g. 9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: "Please enter your email address" },
                        { type: "email", message: "Enter a valid email address" },
                      ]}
                    >
                      <Input placeholder="e.g. rajesh@company.com" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 6: Images */}
              <div style={getStepStyle(6)}>
                {/* Thumbnail */}
                <Form.Item
                  name="thumbnail"
                  label={
                    <span className="edit-stall-label-with-badge">
                      Thumbnail
                      <span className="edit-stall-badge-primary">Main Image</span>
                    </span>
                  }
                  valuePropName="fileList"
                  getValueFromEvent={normalizeUpload}
                  rules={[{ required: true, message: "Please upload a thumbnail image" }]}
                >
                  <Upload.Dragger
                    maxCount={1}
                    accept="image/png,image/jpeg"
                    className="edit-stall-upload"
                    fileList={thumbnailList}
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: newFileList }) => {
                      if (newFileList.length > 0) {
                        const file = newFileList[0];
                        setThumbnailList([
                          {
                            uid: "thumb-" + Date.now(),
                            name: file.name,
                            url: file.originFileObj
                              ? URL.createObjectURL(file.originFileObj)
                              : file.url,
                            originFileObj: file.originFileObj,
                            status: "done",
                          },
                        ]);
                        setRemovedThumbnail(false);
                      } else {
                        setThumbnailList([]);
                        setRemovedThumbnail(true);
                      }
                    }}
                    onRemove={() => {
                      setThumbnailList([]);
                      setRemovedThumbnail(true);
                    }}
                  >
                    <div className="edit-stall-upload-inner">
                      <p className="edit-stall-upload-title">Upload Thumbnail</p>
                      <p className="edit-stall-upload-hint">
                        This is the first image users see · PNG, JPEG
                      </p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>

                {/* Gallery images */}
                <Form.Item
                  name="images"
                  valuePropName="fileList"
                  label={
                    <span className="edit-stall-label-with-badge">
                      Stall Images
                      <span className="edit-stall-badge-secondary">Up to 5</span>
                    </span>
                  }
                  getValueFromEvent={normalizeUpload}
                  rules={[
                    {
                      validator: () => {
                        if (fileList.length === 0)
                          return Promise.reject("Please upload at least one image of the stall");
                        if (fileList.length > 5)
                          return Promise.reject("Maximum 5 images allowed");
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload.Dragger
                    className="edit-stall-upload"
                    maxCount={5}
                    accept="image/png,image/jpeg"
                    multiple
                    fileList={fileList}
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={({ fileList: newFileList }) => {
                      setFileList(newFileList);
                    }}
                    onRemove={(file) => {
                      if (file.url && !file.originFileObj) {
                        setRemovedImages((prev) => [...prev, file.url]);
                      }
                      return true;
                    }}
                  >
                    <div className="edit-stall-upload-inner">
                      <p className="edit-stall-upload-title">Upload Stall Images</p>
                      <p className="edit-stall-upload-hint">Add up to 5 images · PNG, JPEG</p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>
              </div>

              {/* Navigation buttons */}
              <div className="edit-stall-btns">
                {current > 0 && (
                  <Button
                    onClick={prev}
                    className="edit-stall-btn-prev"
                    icon={<ArrowLeftOutlined />}
                    disabled={autoDetecting}
                  >
                    Previous
                  </Button>
                )}
                <div className="edit-stall-btns-right">
                  {current < steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={next}
                      className="edit-stall-btn-next"
                      disabled={autoDetecting}
                    >
                      Next →
                    </Button>
                  )}
                  {current === steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={async () => {
                        if (thumbnailList.length === 0) {
                          message.error("Please upload a thumbnail image");
                          return;
                        }
                        if (fileList.length === 0) {
                          message.error("Please upload at least one image");
                          return;
                        }
                        form.submit();
                      }}
                      loading={loading}
                      disabled={loading}
                      icon={<SaveOutlined />}
                      className="edit-stall-btn-submit"
                    >
                      {loading ? "Updating..." : "Update Stall"}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
}

export default EditStall;